import React, { useEffect, useMemo, useState } from 'react';
import type { TimeRange } from '../../types/dashboard';
import {
  getDashboardWidgetTimeframe,
  getWidgetTimeframeTrailingDays,
  type WidgetTimeframeToken,
} from '../../lib/dashboardTimeRange';
import { computeTradeLevelStats, filterTradesByWidgetTimeframe } from '../../lib/trades/tradeLevelStats';

type DailyData = {
  // profit/loss value for the day/trade
  pnl?: number;
  // possible date representations used by the data source
  date?: string | number;
  t?: string | number;
  timestamp?: string | number;
};

interface NumberOfWinningTradesWidgetProps {
  dailyData: DailyData[];
  timeRange?: TimeRange;
  /** Authoritative trade records; when provided, counts individual winning trades. */
  trades?: any[];
}

// Sparkline helper removed because it was unused.

export const NumberOfWinningTradesWidget: React.FC<NumberOfWinningTradesWidgetProps> = ({ dailyData, timeRange, trades }) => {
  const [timeframe, setTimeframe] = useState<WidgetTimeframeToken>('7D');

  useEffect(() => {
    if (timeRange) setTimeframe(getDashboardWidgetTimeframe(timeRange));
  }, [timeRange]);

  useEffect(() => {
    const handler = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail as any;
        if (!detail || detail.id !== 'numberOfWinningTrades') return;
        const val = detail.value as WidgetTimeframeToken | string;
        if (val === '24H' || val === '7D' || val === '30D' || val === '12M' || val === 'YTD') setTimeframe(val);
      } catch {}
    };
    window.addEventListener('widget-timeframe:selected', handler as EventListener);
    return () => window.removeEventListener('widget-timeframe:selected', handler as EventListener);
  }, []);

  // Filter data only for the numeric value; timeframe buttons affect only the count.
  const filtered = useMemo(() => {
    if (!Array.isArray(dailyData) || dailyData.length === 0) return [] as typeof dailyData;
    const now = Date.now();
    const tfDays = getWidgetTimeframeTrailingDays(timeframe);
    const tfMs = tfDays * 24 * 60 * 60 * 1000;
    const startOfYearMs = (() => {
      const nowDate = new Date();
      const start = new Date(nowDate.getFullYear(), 0, 1, 0, 0, 0, 0);
      return start.getTime();
    })();
    const hasDate = dailyData.some(d => (d as any).date || (d as any).t || (d as any).timestamp);
    if (hasDate) {
      return dailyData.filter(d => {
        const raw = (d as any).date ?? (d as any).t ?? (d as any).timestamp;
        const ts = typeof raw === 'number' ? raw : (typeof raw === 'string' ? Date.parse(raw) : NaN);
        if (!Number.isFinite(ts)) return false;
        if (timeframe === 'YTD') return ts >= startOfYearMs;
        return ts >= (now - tfMs);
      });
    }
    // Fallback for datasets without date fields: for YTD, take up to last 365 entries; otherwise use tfDays entries
    if (timeframe === 'YTD') {
      const max = Math.min(dailyData.length, 365);
      return dailyData.slice(-max);
    }
    const count = Math.min(dailyData.length, tfDays);
    return dailyData.slice(-count);
  }, [dailyData, timeframe]);

  const winningTrades = useMemo(() => {
    if (Array.isArray(trades) && trades.length > 0) {
      const stats = computeTradeLevelStats(filterTradesByWidgetTimeframe(trades, timeframe));
      return stats.wins;
    }
    return (filtered || []).filter(d => (d as any).pnl > 0).length;
  }, [filtered, trades, timeframe]);

  // Static chart (visual only) — matches screenshot: red line with translucent fill anchored at the bottom.
  const staticPath = 'M0 24 L20 22 L40 26 L60 21 L80 24 L100 22 L120 25 L140 28 L160 24 L180 16 L200 18 L220 24 L240 22 L260 26 L280 28 L300 24';

  return (
    <div className="h-full w-full flex flex-col justify-center items-center p-4 relative overflow-hidden">
      {/* timeframe buttons removed per request */}

      <span className="relative z-10 text-3xl sm:text-4xl lg:text-5xl font-bold text-emerald-500 dark:text-emerald-400">
        {winningTrades}
      </span>

      {/* Static chart at the bottom */}
      <div className="absolute left-0 right-0 bottom-0 h-20">
        <svg viewBox="0 0 300 40" preserveAspectRatio="none" className="w-full h-full">
          <defs>
            <linearGradient id="staticAreaGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#16a34a" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#16a34a" stopOpacity="0.02" />
            </linearGradient>
          </defs>
          <path d={`${staticPath} L300 40 L0 40 Z`} fill="url(#staticAreaGrad)" />
          <path d={staticPath} fill="none" stroke="#16a34a" strokeWidth={1} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
};
